<script lang="ts">
  import Modal from '../../ui/Modal.svelte';
  import { roomsQuery } from '$lib/queries/rooms';
  import { copyInputDeviceProgrammingMutation } from '$lib/mutations/input_devices';
  import { toast } from '$lib/state/toast.svelte';
  import { getErrorMessage } from '$lib/api';
  import type { InputDeviceResponse } from '$lib/types/input_device';
  import type { InputKeyResponse } from '$lib/types/input_key';

  let {
    open = false,
    targetDevice = null,
    candidates = [],
    onclose = undefined,
    onsuccess = undefined
  }: {
    open?: boolean;
    targetDevice?: InputDeviceResponse | null;
    candidates?: InputDeviceResponse[];
    onclose?: () => void;
    onsuccess?: () => void;
  } = $props();

  const unitRooms = roomsQuery();
  const copyProgramming = copyInputDeviceProgrammingMutation();

  let selectedSourceId = $state<number | null>(null);

  const roomsById = $derived(new Map((unitRooms.data ?? []).map((r) => [r.id, r.name])));

  const targetKeyCount = $derived(targetDevice?.keys?.length ?? 0);

  const sortedCandidates = $derived.by(() => {
    const list = [...candidates];
    list.sort((a, b) => {
      const aMatch = (a.keys?.length ?? 0) === targetKeyCount ? 0 : 1;
      const bMatch = (b.keys?.length ?? 0) === targetKeyCount ? 0 : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
      return (a.name ?? '').localeCompare(b.name ?? '');
    });
    return list;
  });

  $effect(() => {
    if (!open) {
      selectedSourceId = null;
      return;
    }
    if (sortedCandidates.length === 1) {
      selectedSourceId = sortedCandidates[0].id;
    }
  });

  function programmedKeyCount(keys: InputKeyResponse[] | undefined) {
    return (keys ?? []).filter(
      (k) => (k.actions ?? []).some((a) => a.event_type === 'on' || a.event_type === 'off')
    ).length;
  }

  function deviceLabel(d: InputDeviceResponse) {
    const room = d.location_room != null ? roomsById.get(d.location_room) : null;
    const addr = d.keys?.[0]?.address;
    const parts = [d.name || `Device ${d.id}`];
    if (room) parts.push(room);
    if (addr != null) parts.push(`addr ${addr}`);
    return parts.join(' · ');
  }

  async function confirmCopy() {
    if (!targetDevice || selectedSourceId == null) return;
    try {
      await copyProgramming.mutateAsync({
        targetId: targetDevice.id,
        sourceDeviceId: selectedSourceId
      });
      toast.success('Programming copied');
      onsuccess?.();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }
</script>

<Modal {open} size="md" onclose={() => onclose?.()}>
  {#snippet header()}
    <h3 class="text-base font-medium text-text-primary">Copy programming</h3>
    <p class="text-xs text-text-muted mt-0.5">
      Copy button names and scene assignments from another keypad. Keys are matched by button
      index. Existing programming on this keypad is replaced for matching buttons.
    </p>
    <p class="text-xs text-text-muted mt-1">
      LED feedback tied to removed button actions may be cleared; existing actions are updated in
      place when possible.
    </p>
  {/snippet}

  {#snippet children()}
    {#if sortedCandidates.length === 0}
      <p class="text-sm text-text-muted">No other keypads in this unit to copy from.</p>
    {:else}
      <fieldset class="space-y-2">
        <legend class="text-2xs uppercase tracking-wide text-text-muted mb-2">
          Copy from
        </legend>
        {#each sortedCandidates as d (d.id)}
          {@const keyCount = d.keys?.length ?? 0}
          {@const sameLayout = keyCount === targetKeyCount}
          {@const programmed = programmedKeyCount(d.keys)}
          <label
            class="flex items-start gap-3 rounded border px-3 py-2 cursor-pointer transition-colors duration-100 {selectedSourceId ===
            d.id
              ? 'border-accent-primary bg-bg-selected'
              : 'border-border-default hover:bg-bg-hover'}"
          >
            <input
              type="radio"
              name="copy-source"
              class="mt-1"
              value={d.id}
              checked={selectedSourceId === d.id}
              onchange={() => (selectedSourceId = d.id)}
            />
            <span class="flex-1 min-w-0">
              <span class="block text-sm text-text-primary truncate">{deviceLabel(d)}</span>
              <span class="block text-xs text-text-muted mt-0.5">
                {keyCount} button{keyCount === 1 ? '' : 's'}
                {#if programmed > 0}
                  · {programmed} programmed
                {/if}
                {#if !sameLayout && targetKeyCount > 0}
                  · partial copy (this keypad has {targetKeyCount})
                {/if}
              </span>
            </span>
          </label>
        {/each}
      </fieldset>
    {/if}
  {/snippet}

  {#snippet footer()}
    <div class="flex justify-end gap-2">
      <button type="button" class="btn-secondary text-sm" onclick={() => onclose?.()}>
        Cancel
      </button>
      <button
        type="button"
        class="btn-primary text-sm"
        disabled={copyProgramming.isPending || selectedSourceId == null || !targetDevice}
        onclick={confirmCopy}
      >
        {copyProgramming.isPending ? 'Copying…' : 'Copy programming'}
      </button>
    </div>
  {/snippet}
</Modal>
